import React from "react";
import { Box } from "@mui/material";
import { useLegalEntityAuditGroupManagementReport } from "./LegalEntityAuditGroupManagementReport.hooks";
import ManagementReportDescription from "@common/components/managementReport/managementReportDescription/ManagementReportDescription";
import ManagementReportPage from "@common/components/managementReport/ManagementReportPage";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import ManagementReportInProgressAudits from "./managementReportInProgressAudits/ManagementReportInProgressAudits";
import { LegalEntityAuditStatus } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import ManagementReportCompletedAudits from "./managementReportCompletedAudits/ManagementReportCompletedAudits";
import ManagementReportCharts from "@common/components/managementReport/managementReportCharts/ManagementReportCharts";
import ManagementReportExcludedAudits from "./managementReportExcludedAudits/ManagementReportExcludedAudits";
import ManagementReportSummary from "@common/components/managementReport/managementReportSummary/ManagementReportSummary";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";

function LegalEntityAuditGroupManagementReport() {
    const { translateString } = useTranslation();
    const { customerName } = useUserContext();
    const { isLoading, data, isPdf, chartData } = useLegalEntityAuditGroupManagementReport();

    return (
        <ManagementReportPage
            isLoading={isLoading || data === undefined}
            isPdf={isPdf}
            groupName={data?.name ?? ""}
            sender={data?.sender ?? customerName}
            deadline={data?.deadline}
        >
            {data !== undefined && (
                <>
                    <ManagementReportDescription scoreEnabled={data.scoreEnabled} auditName={data.name} variant="audit" />

                    <ManagementReportSummary
                        isPdf={isPdf}
                        sent={data.sent}
                        deadline={data.deadline}
                        progress={data.progress}
                        templateId={data.auditTemplateId}
                        templateName={data.auditTemplate}
                        templateLabelKey="auditTemplate"
                        respondentLabelKey="legalEntities"
                        respondentCount={data.numberOfLegalEntities}
                        openedCount={data.openedAuditsCount}
                        submittedCount={data.submittedAuditsCount}
                        excludedCount={data.excludedAuditsCount}
                        responsible={data.responsible}
                        sender={data.sender}
                        senderId={data.senderId}
                        scoreEnabled={data.scoreEnabled}
                        overallRiskLevel={data.overallRiskLevel}
                        overallRiskLevelName={data.overallRiskLevelName}
                        overallRiskScore={data.overallRiskScore}
                    />

                    {chartData !== undefined && (
                        <ManagementReportCharts
                            isPdf={isPdf}
                            data={{
                                averageRiskLevel: data.overallRiskLevel,
                                averageScore: data.overallRiskScore,
                                averageScoreName: data.overallRiskLevelName,
                                averageStatus: LegalEntityAuditStatus.Submitted,
                            }}
                            intervalScoreChartData={chartData}
                        />
                    )}

                    <Box className="page-break-before">
                        <DotLegalHeader marginTop={isPdf ? 1 : 4} marginBottom={0} headerStyle="xxxl">
                            {translateString("overviewOfIndividualRespondents")}
                        </DotLegalHeader>
                        <ManagementReportCompletedAudits
                            scoreEnabled={data.scoreEnabled}
                            isLoading={isLoading}
                            audits={data.audits.filter((x) => x.status === LegalEntityAuditStatus.Submitted)}
                            isPdf={isPdf}
                        />

                        <ManagementReportInProgressAudits
                            isPdf={isPdf}
                            isLoading={isLoading}
                            audits={data.audits.filter(
                                (x) =>
                                    !x.closed &&
                                    (x.status === LegalEntityAuditStatus.InProgress ||
                                        x.status === LegalEntityAuditStatus.Other ||
                                        x.status === LegalEntityAuditStatus.Opened ||
                                        x.status === LegalEntityAuditStatus.Sent)
                            )}
                        />

                        <ManagementReportExcludedAudits isPdf={isPdf} isLoading={isLoading} audits={data.audits.filter((x) => x.closed)} />
                    </Box>
                </>
            )}
        </ManagementReportPage>
    );
}

export default LegalEntityAuditGroupManagementReport;
